import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, Loader2, Car, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';

import { ENABLE_PHASE2_VERIFICATION } from '../featureFlags';
import { unsplash } from '../lib/images';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const Register = () => {
  const navigate = useNavigate();
  // Phone/OTP registration is Phase 2: the backend's OTP route needs Twilio, which isn't
  // provisioned, so offering the tab in GTM v1 would be a dead end. Default to email.
  const [authMethod, setAuthMethod] = useState(ENABLE_PHASE2_VERIFICATION ? 'phone' : 'email');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [demoOtp, setDemoOtp] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      });

      if (res.ok) {
        const user = await res.json();
        toast.success('Account created successfully!');
        navigate('/dashboard', { state: { user } });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Registration failed');
      }
    } catch (e) {
      console.error('Registration error:', e);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone })
      });

      if (res.ok) {
        const data = await res.json();
        setOtpSent(true);
        setDemoOtp(data.demo_otp);
        toast.success('OTP sent to your phone');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send OTP');
      }
    } catch (e) {
      console.error('OTP request error:', e);
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: formData.phone, otp })
      });

      if (res.ok) {
        const user = await res.json();
        toast.success('Verified successfully!');
        navigate('/dashboard', { state: { user }, replace: true });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Invalid OTP');
      }
    } catch (e) {
      console.error('OTP verify error:', e);
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img 
              src="/levr-logo.png"
              alt="Levr"
              className="h-10 invert"
            />
          </Link>

          <h1 className="font-outfit text-3xl font-bold text-black mb-2">
            Create your account
          </h1>
          <p className="text-gray-600 mb-8">
            Start comparing finance options today
          </p>

          {/* Google Sign Up */}
          <Button
            data-testid="google-signup-btn"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full h-12 border-gray-200 hover:bg-white mb-6"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <Separator />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-white0">
              or
            </span>
          </div>

          {/* Auth Method Tabs */}
          <Tabs value={authMethod} onValueChange={setAuthMethod} className="w-full">
            {ENABLE_PHASE2_VERIFICATION && (
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="phone" data-testid="phone-tab">
                  <Phone className="w-4 h-4 mr-2" />
                  Phone
                </TabsTrigger>
                <TabsTrigger value="email" data-testid="email-tab">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </TabsTrigger>
              </TabsList>
            )}

            {/* Phone Registration */}
            <TabsContent value="phone">
              {!otpSent ? (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="phone" className="text-stone-700">Mobile Number</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="phone-input"
                        placeholder="021 123 4567"
                        value={formData.phone}
                        onChange={handleChange}
                        className="pl-10 h-12"
                      />
                    </div>
                    <p className="text-xs text-white0 mt-1">We'll send you a verification code</p>
                  </div>

                  <Button
                    onClick={handleRequestOTP}
                    data-testid="request-otp-btn"
                    disabled={loading}
                    className="w-full h-12 bg-black hover:bg-gray-900 rounded-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Verification Code'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-stone-700">Enter Verification Code</Label>
                    <p className="text-sm text-white0 mb-4">
                      Code sent to {formData.phone}
                    </p>
                    
                    <div className="flex justify-center mb-4">
                      <InputOTP 
                        maxLength={6} 
                        value={otp} 
                        onChange={setOtp}
                        data-testid="otp-input"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    {/* Demo OTP Display */}
                    {demoOtp && (
                      <div className="bg-lime-50 border border-lime-200 rounded-lg p-3 text-center mb-4">
                        <p className="text-sm text-lime-700">
                          Demo OTP: <strong className="text-lg">{demoOtp}</strong>
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleVerifyOTP}
                    data-testid="verify-otp-btn"
                    disabled={loading || otp.length !== 6}
                    className="w-full h-12 bg-black hover:bg-gray-900 rounded-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Continue'
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => { setOtpSent(false); setOtp(''); }}
                    className="w-full text-gray-600"
                  >
                    Change number
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Email Registration */}
            <TabsContent value="email">
              <form onSubmit={handleEmailRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-stone-700">Full Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      data-testid="name-input"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="email-input"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-stone-700">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="password-input"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10 h-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={loading}
                  className="w-full h-12 bg-black hover:bg-gray-900 rounded-full mt-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-gray-700 font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("${unsplash('photo-1647649641463-724358c4387e', { w: 1600, q: 65 })}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <h2 className="font-outfit text-4xl font-bold text-white mb-4">
              Quick & Easy Sign Up
            </h2>
            <p className="text-emerald-200 text-lg max-w-md">
              Create your account in seconds and start comparing finance options.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
